import {
    Users, Briefcase, ClipboardList, UserCog, Brain, CalendarClock, GraduationCap,
    Code2, Database, Cloud, Layers, Settings2, LineChart, FlaskConical,
    Search, UserSearch, SlidersHorizontal, Send, Handshake, ShieldCheck, X, Check
} from "lucide-react";
import PageMeta from "../components/layout/PageMeta";
import PageHero from "../components/sections/PageHero";
import FinalCTA from "../components/sections/FinalCTA";

const whatWeStaff = [
    { icon: <Briefcase className="w-5 h-5" />, title: "IT Staffing", copy: "Specialised engineering, data and AI talent matched to your stack, seniority bar and timezone." },
    { icon: <ClipboardList className="w-5 h-5" />, title: "Contract Staffing", copy: "Flexible contract resources for projects, surges and short-term capability gaps — onboarded in days." },
    { icon: <UserCog className="w-5 h-5" />, title: "C2C / Vendor Support", copy: "End-to-end compliance, paperwork and lifecycle management for C2C and W2 engagements." },
    { icon: <GraduationCap className="w-5 h-5" />, title: "Bench Talent Enablement", copy: "Upskilling, deployment readiness and visibility for partners managing bench resources." },
];

const howAIImproves = [
    { icon: <Brain className="w-5 h-5" />, title: "AI-powered matching", copy: "TalentIQX scores, ranks and shortlists candidates against role context — explainable to recruiters and hiring managers." },
    { icon: <Layers className="w-5 h-5" />, title: "Managed routing", copy: "Intelligent distribution of requirements to correct sourcing channels and search partners." },
    { icon: <UserSearch className="w-5 h-5" />, title: "Recruiter screening support", copy: "AI highlights gaps and suggests technical validation questions based on resume content." },
    { icon: <CalendarClock className="w-5 h-5" />, title: "Interview coordination", copy: "Logistics, panels and structured feedback — managed without the email ping-pong." },
    { icon: <SlidersHorizontal className="w-5 h-5" />, title: "Requirement parsing", copy: "Instantly translate natural language job briefs into structured keyword and skill taxonomies." },
    { icon: <ShieldCheck className="w-5 h-5" />, title: "Shortlist quality checks", copy: "Automated verification against rate constraints, notice periods, and geographic requirements." },
];

const qualityGate = [
    { title: "Weak role match", copy: "Candidates who don't align closely with core technical stack and requirements." },
    { title: "Inflated experience", copy: "Discrepancies in skill duration or mismatched complexity levels." },
    { title: "Missing domain fit", copy: "Lack of industry-specific compliance or vertical operational experience." },
    { title: "Rate / location mismatch", copy: "Compensation expectations or geographic preferences outside job bounds." },
    { title: "Poor communication", copy: "Sub-par collaboration skills during screening conversations." },
    { title: "Resume buzzword stuffing", copy: "Keywords added to resumes without actual project experience to support them." },
];

const lifecycle = [
    { icon: <ClipboardList className="w-4 h-4" />, title: "Requirement intake", copy: "Capture role context, must-haves, success signals and timeline — in a single structured brief." },
    { icon: <Search className="w-4 h-4" />, title: "Multi-channel sourcing", copy: "Our network, partners and proprietary database — searched in parallel, deduplicated in TalentIQX." },
    { icon: <Brain className="w-4 h-4" />, title: "AI-assisted matching", copy: "TalentIQX ranks candidates against role context with explainable scoring a recruiter can defend." },
    { icon: <UserSearch className="w-4 h-4" />, title: "Recruiter screening", copy: "Senior recruiters validate fit, motivation, availability and red flags." },
    { icon: <Send className="w-4 h-4" />, title: "Submission & interviews", copy: "Curated shortlists, panel coordination and structured feedback inside RecruitIQX." },
    { icon: <Handshake className="w-4 h-4" />, title: "Onboarding support", copy: "Paperwork, compliance and 30/60/90-day check-ins — so first-90-day churn stays low." },
];

const categories = [
    { title: "AI/ML Engineers", chips: ["LLM Apps", "MLOps", "RAG"], icon: <Brain className="w-4 h-4" /> },
    { title: "Data Engineers", chips: ["Spark", "Snowflake", "Airflow"], icon: <Database className="w-4 h-4" /> },
    { title: "Cloud Engineers", chips: ["AWS", "Azure", "GCP"], icon: <Cloud className="w-4 h-4" /> },
    { title: "Full Stack Developers", chips: ["React", "Node", "Python"], icon: <Code2 className="w-4 h-4" /> },
    { title: "DevOps Engineers", chips: ["K8s", "Terraform", "CI/CD"], icon: <Settings2 className="w-4 h-4" /> },
    { title: "Business Analysts", chips: ["BFSI", "Healthcare", "Retail"], icon: <LineChart className="w-4 h-4" /> },
    { title: "QA Engineers", chips: ["Automation", "Performance", "Security"], icon: <FlaskConical className="w-4 h-4" /> },
    { title: "ERP / CRM Consultants", chips: ["SAP", "Oracle", "Salesforce"], icon: <Layers className="w-4 h-4" /> },
];

export default function TalentSolutions() {
    return (
        <div className="pqx-page-enter">
            <PageMeta
                title="Talent Solutions"
                description="AI-assisted technology staffing — specialised engineering, data and AI talent sourced, scored and shortlisted by TalentIQX, validated by senior recruiters."
            />

            <PageHero
                eyebrow="Talent Solutions"
                title="AI-assisted staffing —"
                titleAccent="specialised, vetted, shortlisted in hours."
                description="We don't supply manpower. We deliver senior, vetted technology talent matched against your role context by TalentIQX — and validated by recruiters who understand your domain."
                primaryCta={{ to: "/contact", label: "Send a requirement" }}
                secondaryCta={{ to: "/products/talentiqx", label: "Inside TalentIQX" }}
                bullets={["Target 48-hour shortlist", "AI-scored matches", "Senior recruiter validation", "Managed compliance"]}
            />

            {/* Promise strip */}
            <section className="bg-white border-b border-[#303030]/8 py-10 md:py-12">
                <div className="pqx-container grid sm:grid-cols-3 gap-6 text-center">
                    {[
                        { v: "48h", l: "Target shortlist", sub: "For qualified roles" },
                        { v: "3.5×", l: "Recruiter productivity", sub: "On TalentIQX-enabled engagements" },
                        { v: "<8%", l: "First-90-day churn", sub: "Across placed engineers" },
                    ].map((s) => (
                        <div key={s.l}>
                            <p className="pqx-stat-num text-4xl md:text-5xl text-[#FF4040]">{s.v}</p>
                            <p className="mt-3 text-[14px] font-semibold text-[#303030]">{s.l}</p>
                            <p className="text-[12px] text-[#303030]/55 mt-0.5">{s.sub}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* What we staff */}
            <section className="pqx-section bg-[#FAFAF8]" data-testid="talent-what-we-staff">
                <div className="pqx-container">
                    <div className="max-w-3xl">
                        <span className="pqx-eyebrow">Services</span>
                        <h2 className="pqx-h2 mt-4 text-[#303030]">
                            What we staff.
                        </h2>
                        <p className="mt-5 text-[#303030]/70 text-base md:text-lg leading-relaxed max-w-2xl">
                            Senior software developers, data practitioners, and AI engineers delivered to align with your project timelines and organizational structure.
                        </p>
                    </div>

                    <div className="mt-12 md:mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                        {whatWeStaff.map((s, i) => (
                            <div key={s.title} data-testid={`talent-service-${i}`} className="pqx-card-elevated !p-6 group bg-white border border-[#303030]/5 rounded-2xl">
                                <span className="w-11 h-11 grid place-items-center rounded-xl bg-[#303030]/5 text-[#303030] group-hover:bg-[#FF4040] group-hover:text-white transition-colors">
                                    {s.icon}
                                </span>
                                <h3 className="pqx-display text-lg text-[#303030] mt-5">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm text-[#303030]/70 leading-relaxed">
                                    {s.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How AI improves delivery */}
            <section className="pqx-section bg-white" data-testid="talent-ai-advantage">
                <div className="pqx-container">
                    <div className="max-w-3xl">
                        <span className="pqx-eyebrow !text-[#FF4040]">AI Advantage</span>
                        <h2 className="pqx-h2 mt-4 text-[#303030]">
                            How AI improves delivery.
                        </h2>
                        <p className="mt-5 text-[#303030]/70 text-base md:text-lg leading-relaxed max-w-2xl">
                            We use the TalentIQX engine to remove the administrative latency of typical agency workflows, enabling recruiters to focus on candidate screening and domain matching.
                        </p>
                    </div>

                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {howAIImproves.map((s, i) => (
                            <div key={s.title} data-testid={`talent-ai-service-${i}`} className="pqx-card-elevated !p-6 group bg-[#FAFAF8] border border-[#303030]/5 rounded-2xl">
                                <span className="w-11 h-11 grid place-items-center rounded-xl bg-[#303030]/5 text-[#303030] group-hover:bg-[#FF4040] group-hover:text-white transition-colors">
                                    {s.icon}
                                </span>
                                <h3 className="pqx-display text-lg text-[#303030] mt-5">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm text-[#303030]/70 leading-relaxed">
                                    {s.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quality Gate */}
            <section className="pqx-section bg-[#FAFAF8]" data-testid="talent-quality-gate">
                <div className="pqx-container">
                    <div className="max-w-3xl">
                        <span className="pqx-eyebrow">Quality Gate</span>
                        <h2 className="pqx-h2 mt-4 text-[#303030]">
                            What we reject before it reaches you.
                        </h2>
                        <p className="mt-5 text-[#303030]/70 text-base md:text-lg leading-relaxed max-w-2xl">
                            Our matching pipeline enforces a strict evaluation layer so that only qualified profiles progress to submissions. Here is what we actively filter out:
                        </p>
                    </div>

                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {qualityGate.map((item, i) => (
                            <div key={item.title} data-testid={`talent-reject-${i}`} className="pqx-card-elevated !p-6 bg-white border-l-2 border-[#FF4040]/40 rounded-r-2xl">
                                <div className="flex items-center gap-2 text-[#FF4040]">
                                    <X className="w-4 h-4 shrink-0" />
                                    <h3 className="pqx-display text-base text-[#303030] font-semibold">{item.title}</h3>
                                </div>
                                <p className="mt-3 text-sm text-[#303030]/70 leading-relaxed">
                                    {item.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Candidate lifecycle */}
            <section className="pqx-section bg-white" data-testid="talent-lifecycle">
                <div className="pqx-container">
                    <div className="max-w-3xl">
                        <span className="pqx-eyebrow">Accountability</span>
                        <h2 className="pqx-h2 mt-4 text-[#303030]">
                            From requirement intake to onboarding — tracked, screened, and accountable.
                        </h2>
                        <p className="mt-5 text-[#303030]/70 text-base md:text-lg leading-relaxed">
                            Recruiters and clients share a single view across sourcing, matching,
                            interview coordination and onboarding — with AI handling the repetitive
                            lift.
                        </p>
                    </div>

                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {lifecycle.map((l, i) => (
                            <div key={l.title} data-testid={`talent-lifecycle-${i}`} className="relative pqx-card-elevated !p-6">
                                <div className="flex items-center gap-3">
                                    <span className="pqx-stat-num text-2xl text-[#FF4040]">0{i + 1}</span>
                                    <span className="w-9 h-9 grid place-items-center rounded-xl bg-[#303030]/5 text-[#303030]">
                                        {l.icon}
                                    </span>
                                </div>
                                <h3 className="pqx-display text-lg text-[#303030] mt-5">
                                    {l.title}
                                </h3>
                                <p className="mt-2 text-sm text-[#303030]/70 leading-relaxed">
                                    {l.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Categories */}
            <section className="pqx-section pqx-dark-panel relative overflow-hidden" data-testid="talent-categories">
                <div className="absolute inset-0 pqx-grid-bg-dark opacity-40" />
                <div className="pqx-container relative z-10">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
                        <div className="max-w-3xl">
                            <span className="pqx-eyebrow !text-[#FF4040]">Hiring categories</span>
                            <h2 className="pqx-h2 mt-4 text-white">
                                Talent we place every week.
                            </h2>
                        </div>
                        <p className="text-white/68 max-w-md text-sm md:text-base leading-relaxed">
                            Actively-vetted pipelines across modern enterprise technology categories
                            — not a generic "candidate database".
                        </p>
                    </div>

                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {categories.map((c, i) => (
                            <div key={c.title} data-testid={`talent-category-${i}`} className="pqx-glass-dark rounded-2xl p-5">
                                <span className="w-9 h-9 grid place-items-center rounded-xl bg-[#FF4040]/15 text-[#FF4040]">
                                    {c.icon}
                                </span>
                                <h3 className="pqx-display text-lg mt-4 text-white">{c.title}</h3>
                                <div className="mt-4 flex flex-wrap gap-1.5">
                                    {c.chips.map((chip) => (
                                        <span key={chip} className="text-[11px] px-2 py-0.5 rounded-full bg-white/8 text-white/80">
                                            {chip}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <FinalCTA
                title="Need specialised talent — fast?"
                description="Target 48-hour shortlist for qualified roles."
                primary={{ to: "/contact", label: "Send a requirement" }}
                secondary={{ to: "/products/talentiqx", label: "Inside TalentIQX" }}
            />
        </div>
    );
}
